import * as React from 'react';
import { I18n } from 'react-redux-i18n';
import { SpreadsheetExportNewLineType, SpreadsheetExportType } from '@modules/spreadsheets/dto/spreadsheet/export-type';
import { SpreadsheetDetailsExportDataParams } from '@modules/spreadsheets/actions/spreadsheet';
import { SpreadsheetDataExportResponseDto } from '@modules/spreadsheets/dto/data-export/response';
import { HttpErrorDto } from '@common/dto/http';

export type DataExportProps = {
  close: () => void;
  showErrorMessage: (message: string) => void;
  onExport: (data: Omit<SpreadsheetDetailsExportDataParams, 'columnModel' | 'queryModel' | 'filter' | 'pss'>) => Promise<SpreadsheetDataExportResponseDto>;
};

export const useController = (props: DataExportProps) => {
  const DEFAULT_DELIMITER = undefined;

  const RESTRICTED_NEWLINES: Partial<Record<SpreadsheetExportType, Array<SpreadsheetExportNewLineType>>> = {
    [SpreadsheetExportType.txtComma]: [SpreadsheetExportNewLineType.noFormatting] 
  };

  const { onExport, showErrorMessage } = props;

  const settings = React.useMemo(() => {
    return [
      { label: 'Text (Tab Delimited)', id: SpreadsheetExportType.txtTab },
      { label: 'Text (Comma Delimited)', id: SpreadsheetExportType.txtComma },
      { label: 'Text (Other)', id: SpreadsheetExportType.txtOther },
      { label: 'Excel (xls)', id: SpreadsheetExportType.xls },
      { label: 'Excel (xlsx)', id: SpreadsheetExportType.xlsx },
    ].map((x) => ({ ...x, label: I18n.t(x.label) }));
  }, []);

  const options = React.useMemo(() => {
    return [
      { label: 'Include Column Headings', metadata: { showHeaders: true } },
      { label: 'Export Table Rows Only', metadata: { tableRowsOnly: true } },
      { label: 'Export One Row Per Individual', metadata: { oneRowPerIndividual: true } },
    ].map((x) => ({ ...x, id: x.label, label: I18n.t(x.label) }));
  }, []);

  const newlinesOptions = React.useMemo(() => {
    return [
      { label: 'Retain newlines in formatted cells', id: SpreadsheetExportNewLineType.retainFormatted, },
      { label: 'Convert newlines to spaces', id: SpreadsheetExportNewLineType.asSpaces, },
      { label: 'Export newlines without any formatting', id: SpreadsheetExportNewLineType.noFormatting, },
    ].map((x) => ({ ...x, label: I18n.t(x.label) }));
  }, []);

  const [selectedSetting, setSelectedSetting] = React.useState(settings[0]!.id);
  const [selectedNewlinesSetting, setSelectedNewlinesSetting] = React.useState(newlinesOptions[0]!.id);
  const [selectedOptions, setSelectedOptions] = React.useState<Array<string>>([]);
  const [delimiter, setDelimiter] = React.useState<string | undefined>(DEFAULT_DELIMITER);
  const [submitting, setSubmitting] = React.useState(false);

  const handleNewlinesSettingsChange = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    e.persist();

    const { value } = e.target;

    setSelectedNewlinesSetting(Number(value));
  }, []);

  const handleSettingsChange = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    e.persist();

    const { value } = e.target;

    setSelectedSetting(value as SpreadsheetExportType);
    setDelimiter(value === SpreadsheetExportType.txtOther ? '' : DEFAULT_DELIMITER);

    const restrictedNewlinesOptions = RESTRICTED_NEWLINES[value as SpreadsheetExportType];
    const areRestrictedNewlinesOptionsSelected = restrictedNewlinesOptions?.includes(selectedNewlinesSetting);

    if (areRestrictedNewlinesOptionsSelected) {
      const defaultNewlinesOption = newlinesOptions.find((x) => !restrictedNewlinesOptions?.includes(x.id));

      if (defaultNewlinesOption) {
        setSelectedNewlinesSetting(defaultNewlinesOption.id);
      }
    }
  }, [selectedSetting, selectedNewlinesSetting]);

  const handleOptionsChange = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    e.persist();

    const { value } = e.target;

    // Handle mutual exclusion between "Export Table Rows Only" and "Export One Row Per Individual"
    if (value === 'Export Table Rows Only' && selectedOptions.includes('Export One Row Per Individual')) {
      // If selecting "Export Table Rows Only", remove "Export One Row Per Individual" but keep "Include Column Headings"
      const filteredOptions = selectedOptions.filter((x) => x !== 'Export One Row Per Individual');
      setSelectedOptions([...filteredOptions, value]);
    } else if (value === 'Export One Row Per Individual' && selectedOptions.includes('Export Table Rows Only')) {
      // If selecting "Export One Row Per Individual", remove "Export Table Rows Only" but keep "Include Column Headings"
      const filteredOptions = selectedOptions.filter((x) => x !== 'Export Table Rows Only');
      setSelectedOptions([...filteredOptions, value]);
    } else {
      // Normal toggle behavior for other options (including "Include Column Headings")
      const resultSettings = selectedOptions.includes(value)
        ? selectedOptions.filter((x) => x !== value)
        : [...selectedOptions, value];

      setSelectedOptions(resultSettings);
    }
  }, [selectedOptions]);

  const handleDelimiterChange = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    e.persist();

    const { value } = e.target;

    setDelimiter(value);
  }, []);

  const shouldIgnoreNewlines = [SpreadsheetExportType.xls, SpreadsheetExportType.xlsx].includes(selectedSetting);

  const handleExport = async () => {
    try {
      setSubmitting(true);

      const optionsSubmitData = selectedOptions.reduce((acc, id) => {
        const option = options.find((x) => x.id === id);

        return {
          ...acc,
          ...option?.metadata
        };
      }, {});

      const defaultParams = {
        showHeaders: false
      };

      await onExport({
        ...defaultParams,
        delimiter,
        exportType: selectedSetting,
        newLinesOptions: shouldIgnoreNewlines ? undefined : selectedNewlinesSetting,
        ...optionsSubmitData,
      });
      
      setSubmitting(false);
      props.close();
    } catch (err) {
      setSubmitting(false);
      showErrorMessage((err as HttpErrorDto | undefined)?.entity || I18n.t('Failed to export'));
    }
  };

  return {
    settings,
    options,
    selectedSetting,
    handleSettingsChange,
    delimiter,
    handleDelimiterChange,
    selectedOptions,
    handleOptionsChange,
    newlinesOptions,
    shouldIgnoreNewlines,
    selectedNewlinesSetting,
    handleNewlinesSettingsChange,
    submitting,
    handleExport,
    RESTRICTED_NEWLINES,
  };
};